import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Tooltip,
    Chip,
    CircularProgress,
    useTheme,
    Grow,
    Fade,
    Autocomplete
} from '@mui/material';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import {
    Visibility,
    VisibilityOff,
    LockReset as LockResetIcon,
    Security as SecurityIcon,
    Search as SearchIcon,
    AdminPanelSettings as AdminIcon,
    Refresh as RefreshIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { TraerProveedores, getProviderById, getIDWorkmanager, encrypt } from '../FunctionLogin';

export default function Configuracion() {
    const navigate = useNavigate();
    const theme = useTheme();

    // State
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Modal State
    const [openModal, setOpenModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    // Switch User Modal State
    const [openSwitchModal, setOpenSwitchModal] = useState(false);
    const [providersList, setProvidersList] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [switching, setSwitching] = useState(false);

    // Initial Load
    useEffect(() => {
        // Validate Admin Role
        const rol = localStorage.getItem('rol'); // Assuming 'rol' is stored directly or inside a JSON
        // If strict check needed: 
        // if (rol !== 'Admin' && rol !== 'ADMIN') {
        //    navigate('/proveedores');
        //    return;
        // }
        // For now, allowing access to load data, but backend should also protect.

        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Using the path assumed from previous context or as specified by user requirements
            const response = await fetch('https://consumos.talsa.com.co/api/talsa/proveedores/get');
            const data = await response.json();

            if (data.success) {
                // Add unique ID for DataGrid if not present
                const rowsWithId = data.data.map((row, index) => ({
                    id: index,
                    ...row
                }));
                setUsers(rowsWithId);
            } else {
                swal("Error", "No se pudieron cargar los usuarios", "error");
            }
        } catch (error) {
            console.error(error);
            swal("Error de Conexión", "No se pudo conectar con el servidor", "error");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchUsers();
    };

    const handleOpenChangePassword = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedUser(null);
        setNewPassword('');
        setSaving(false);
    };

    const handleChangePassword = async () => {
        if (!newPassword.trim()) {
            swal("Atención", "La contraseña no puede estar vacía", "warning");
            return;
        }

        setSaving(true);
        try {
            const response = await fetch('https://consumos.talsa.com.co/api/talsa/proveedores/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    usuario: selectedUser.usuario,
                    newPassword: newPassword
                })
            });

            const result = await response.json();

            if (result.success) {
                swal("Éxito", "Contraseña actualizada correctamente", "success");
                handleCloseModal();
            } else {
                swal("Error", result.error || "No se pudo actualizar la contraseña", "error");
            }
        } catch (error) {
            console.error(error);
            swal("Error", "Error al intentar cambiar la contraseña", "error");
        } finally {
            setSaving(false);
        }
    };

    // Switch User Functions
    const handleOpenSwitchUser = () => {
        setOpenSwitchModal(true);
        setProvidersList([]);
        setSelectedProvider(null);
        setInputValue('');
    };

    const handleCloseSwitchModal = () => {
        setOpenSwitchModal(false);
        setProvidersList([]);
        setSelectedProvider(null);
        setInputValue('');
        setSwitching(false);
    };

    const handleInputChange = async (event, newInputValue) => {
        setInputValue(newInputValue);

        if (newInputValue.length >= 3) {
            try {
                const result = await TraerProveedores(newInputValue);
                setProvidersList(result);
            } catch (error) {
                console.error('Error buscando proveedores:', error);
            }
        } else {
            setProvidersList([]);
        }
    };

    const handleSwitchUser = async () => {
        if (!selectedProvider) {
            swal("Atención", "Por favor seleccione un proveedor", "warning");
            return;
        }

        setSwitching(true);
        try {
            // Extraer el NIT del provider seleccionado
            const nit = selectedProvider.id || selectedProvider["ID SAP"];

            if (!nit) {
                swal("Error", "No se pudo obtener el ID del proveedor", "error");
                setSwitching(false);
                return;
            }

            // Obtener datos del proveedor
            const result = await getProviderById(nit);

            if (result && result.length > 0) {
                const providerData = result[0];

                // Actualizar localStorage con los datos del nuevo proveedor
                localStorage.setItem("Proveedor", providerData.Proveedor);
                localStorage.setItem("Tproveedor", providerData.Tproveedor);
                localStorage.setItem("Nit", providerData.Nit);
                localStorage.setItem("TipoProveedor", providerData.tipo);
                localStorage.setItem("Pais", providerData.Pais);
                localStorage.setItem("Ciudad", providerData.Ciudad);
                localStorage.setItem("Telefono", providerData.Telefono);

                // Obtener datos adicionales de WorkManager
                const workmanagerData = await getIDWorkmanager(providerData.Proveedor);
                localStorage.setItem("Categoria", workmanagerData[8].Value);
                localStorage.setItem("Criticidad", workmanagerData[9].Value);
                localStorage.setItem("Email", workmanagerData[5].Value);
                localStorage.setItem("Direcc", workmanagerData[3].Value);

                workmanagerData.forEach(element => {
                    if (element.Field === "Radicado") {
                        localStorage.setItem("Radicado", element.Value);
                    }
                });

                // Actualizar la sesión con un nuevo token
                const session = await encrypt("Conectado" + Math.random().toFixed(2));
                Cookies.set('sessionId', session, { sameSite: 'strict' });

                swal("¡Cambio exitoso!", `Has cambiado al proveedor: ${providerData.Tproveedor}`, "success");

                // Recargar la página para aplicar los cambios
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                swal("Error", "No se encontraron datos del proveedor", "error");
            }
        } catch (error) {
            console.error('Error al cambiar de usuario:', error);
            swal("Error", "Error al intentar cambiar de usuario", "error");
        } finally {
            setSwitching(false);
        }
    };

    // Columns for DataGrid
    const columns = [
        {
            field: 'usuario',
            headerName: 'Usuario',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%', backgroundColor: theme.palette.primary.main,
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
                    }}>
                        {params.value ? params.value.charAt(0).toUpperCase() : '?'}
                    </div>
                    <Typography variant="body2" fontWeight={600}>{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'email',
            headerName: 'Correo Electrónico',
            flex: 1.5,
            minWidth: 200
        },
        {
            field: 'rol',
            headerName: 'Rol',
            width: 150,
            renderCell: (params) => (
                <Chip
                    icon={params.value === 'ADMIN' ? <AdminIcon style={{ fontSize: 16 }} /> : undefined}
                    label={params.value || 'N/A'}
                    color={params.value === 'ADMIN' || params.value === 'Admin' ? "error" : "default"}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<LockResetIcon />}
                    onClick={() => handleOpenChangePassword(params.row)}
                    sx={{
                        backgroundColor: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: theme.palette.warning.dark,
                            boxShadow: '0 4px 12px rgba(237, 108, 2, 0.4)'
                        },
                        borderRadius: '20px',
                        textTransform: 'none'
                    }}
                >
                    Cambiar Clave
                </Button>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%', p: 3, maxWidth: 1200, margin: '0 auto', animation: 'fadeIn 0.5s ease-in' }}>
            <Paper
                elevation={3}
                sx={{
                    p: 0,
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    border: '1px solid rgba(0,0,0,0.08)'
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    background: 'linear-gradient(to right, #ffffff, #f9fafb)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AdminIcon color="primary" /> Gestión de Usuarios
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Administración de proveedores y credenciales
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Cambiar usuario conectado">
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<SwapHorizIcon />}
                                onClick={handleOpenSwitchUser}
                                sx={{
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)'
                                    }
                                }}
                            >
                                Cambiar Usuario
                            </Button>
                        </Tooltip>
                        <Tooltip title="Recargar lista">
                            <IconButton onClick={handleRefresh} disabled={refreshing || loading} color="primary" sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                                {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{ height: 600, width: '100%', p: 1 }}>
                    <DataGrid
                        rows={users}
                        columns={columns}
                        loading={loading}
                        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500, placeholder: 'Buscar usuario...' },
                            },
                        }}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        disableRowSelectionOnClick
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f9fafb',
                                borderBottom: '2px solid #eaeaea',
                                fontWeight: 'bold'
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    />
                </Box>
            </Paper>

            {/* Change Password Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                TransitionComponent={Grow}
                PaperProps={{
                    sx: { borderRadius: 3, padding: 1, minWidth: 400 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.primary.main }}>
                    <SecurityIcon /> Cambiar Contraseña
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body1" gutterBottom>
                            Nueva contraseña para: <strong>{selectedUser?.usuario}</strong>
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nueva Contraseña"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            variant="outlined"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            sx={{ mt: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockResetIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseModal} color="inherit" sx={{ borderRadius: '20px' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleChangePassword}
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ borderRadius: '20px', px: 3, textTransform: 'none' }}
                    >
                        {saving ? "Guardando..." : "Actualizar Contraseña"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Switch User Modal */}
            <Dialog
                open={openSwitchModal}
                onClose={handleCloseSwitchModal}
                TransitionComponent={Grow}
                PaperProps={{
                    sx: { borderRadius: 3, padding: 1, minWidth: 500 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.secondary.main }}>
                    <SwapHorizIcon /> Cambiar Usuario Conectado
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body1" gutterBottom color="text.secondary">
                            Busque y seleccione el proveedor al que desea cambiar. No se requiere contraseña.
                        </Typography>
                        <Autocomplete
                            options={providersList}
                            getOptionLabel={(option) => option.label || `${option.id || option["ID SAP"]}-${option["RAZON SOCIAL"] || "N/A"}`}
                            inputValue={inputValue}
                            onInputChange={handleInputChange}
                            onChange={(event, newValue) => {
                                setSelectedProvider(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Buscar Proveedor"
                                    variant="outlined"
                                    placeholder="Ingrese al menos 3 caracteres..."
                                    sx={{ mt: 2 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                            noOptionsText={inputValue.length < 3 ? "Ingrese al menos 3 caracteres" : "No se encontraron proveedores"}
                            fullWidth
                            sx={{ mt: 2 }}
                        />
                        {selectedProvider && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Proveedor seleccionado:
                                </Typography>
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                    {selectedProvider.label || selectedProvider["RAZON SOCIAL"]}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseSwitchModal} color="inherit" sx={{ borderRadius: '20px' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSwitchUser}
                        variant="contained"
                        color="secondary"
                        disabled={switching || !selectedProvider}
                        startIcon={switching ? <CircularProgress size={20} color="inherit" /> : <SwapHorizIcon />}
                        sx={{ borderRadius: '20px', px: 3, textTransform: 'none' }}
                    >
                        {switching ? "Cambiando..." : "Cambiar Usuario"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
